import { type ReactNode, useEffect, useRef, useState } from "react";
import { Tooltip } from "antd";
import type { TooltipPlacement } from "antd/es/tooltip";
import { twMerge } from "tailwind-merge";

interface EllipsisTooltipProps {
  title: ReactNode;
  children: ReactNode;
  className?: string;
  innerClassName?: string;
  placement?: TooltipPlacement;
  delay?: number;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
}

/** 默认把 tooltip 挂到触发元素的父节点，继承所在 Popover/Select 的 stacking context，避免被遮挡 */
const defaultGetPopupContainer = (_triggerNode: HTMLElement): HTMLElement => document.body;

export const EllipsisTooltip = ({
  title,
  children,
  className,
  innerClassName,
  placement = "top",
  delay = 100,
  getPopupContainer = defaultGetPopupContainer,
}: EllipsisTooltipProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isOver, setIsOver] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // 外层宽度动画会导致首帧测量不准，延迟一拍再判断
    const timer = setTimeout(() => {
      setIsOver(el.scrollWidth - el.offsetWidth >= 1);
    }, delay);
    return () => clearTimeout(timer);
  }, [delay, children]);

  return (
    <Tooltip
      placement={placement}
      classNames={{ root: "ellipse-tool-tip" }}
      title={
        <div onClick={(event) => event.stopPropagation()} className="whitespace-pre-wrap">
          {title}
        </div>
      }
      open={isOver ? undefined : false}
      trigger="hover"
      getPopupContainer={getPopupContainer}
    >
      <div className={twMerge("max-w-full", className)}>
        <div ref={ref} className={twMerge("block whitespace-nowrap overflow-hidden text-ellipsis", innerClassName)}>
          {children}
        </div>
      </div>
    </Tooltip>
  );
};
